import {Socket} from "socket.io-client";
import {MediasoupManagerHandle} from "../core/MediasoupManager";
import {SpatialAudioManagerHandle} from "../channels/spatial/SpatialAudioChannel";
import {RoomState} from "../core/room";
import {
  Direction,
  MediaState,
  OdysseyEvent,
  Participant,
  ParticipantsSnapshotEvent,
  Position,
} from "../types";
import {debugLog} from "../utils/debug";

export interface ListenerContext {
  socket: Socket;
  state: RoomState;
  mediasoupManager: MediasoupManagerHandle;
  spatialAudioManager: SpatialAudioManagerHandle;
  emitEvent(event: OdysseyEvent, ...args: any[]): void;
}

export function registerSocketListeners(ctx: ListenerContext): void {
  const {socket, state, mediasoupManager, spatialAudioManager, emitEvent} = ctx;

  // ── Participants snapshot ─────────────────────────────────────────────────
  socket.on("all-participants-update", (payload: ParticipantsSnapshotEvent) => {
    if (!state.room) {
      state.room = {id: payload.roomId, participants: new Map()};
    }

    const activeParticipantIds = new Set<string>();

    for (const snapshot of payload.participants) {
      activeParticipantIds.add(snapshot.participantId);

      let participant = state.room?.participants.get(snapshot.participantId);

      const normalizedPosition = {
        x: snapshot.position?.x ?? 0,
        y: snapshot.position?.y ?? 0,
        z: snapshot.position?.z ?? 0,
      };
      const normalizedDirection = {
        x: snapshot.direction?.x ?? 0,
        y: snapshot.direction?.y ?? 0,
        z: snapshot.direction?.z ?? 1,
      };
      const normalizedMediaState: MediaState = {
        audio: snapshot.mediaState?.audio ?? false,
        video: snapshot.mediaState?.video ?? false,
        sharescreen: snapshot.mediaState?.sharescreen ?? false,
      };

      if (!participant) {
        participant = {
          participantId: snapshot.participantId,
          userId: snapshot.userId,
          deviceId: snapshot.deviceId,
          isLocal:
            state.localParticipant?.participantId === snapshot.participantId,
          audioTrack: undefined,
          videoTrack: undefined,
          producers: new Map(),
          consumers: new Map(),
          position: normalizedPosition,
          direction: normalizedDirection,
          mediaState: normalizedMediaState,
        } as Participant;
      } else {
        participant.userId = snapshot.userId;
        participant.deviceId = snapshot.deviceId;
        participant.position = normalizedPosition;
        participant.direction = normalizedDirection;
        participant.mediaState = normalizedMediaState;
      }

      participant.bodyHeight = snapshot.bodyHeight;
      participant.bodyShape = snapshot.bodyShape;
      participant.userName = snapshot.userName;
      participant.userEmail = snapshot.userEmail;
      participant.isLocal =
        state.localParticipant?.participantId === snapshot.participantId;

      // CRITICAL: Store channel data for huddle detection
      (participant as any).currentChannel =
        (snapshot as any).currentChannel || "spatial";

      state.room?.participants.set(snapshot.participantId, participant);

      if (participant.isLocal) {
        state.localParticipant = participant;
      }
    }

    if (state.room) {
      for (const existingId of Array.from(state.room.participants.keys())) {
        if (!activeParticipantIds.has(existingId)) {
          state.room.participants.delete(existingId);
        }
      }
    }

    const normalizedParticipants = state.room
      ? Array.from(state.room.participants.values())
      : [];

    emitEvent("all-participants-update", normalizedParticipants);
  });

  // ── New participant joined ────────────────────────────────────────────────
  socket.on("new-participant", (participantData: any) => {
    if (state.room) {
      const newParticipant: Participant = {
        participantId: participantData.participantId,
        userId: participantData.userId,
        deviceId: participantData.deviceId,
        position: participantData.position,
        direction: participantData.direction,
        mediaState: participantData.mediaState,
        isLocal: false,
        producers: new Map(),
        consumers: new Map(),
        bodyHeight: participantData.bodyHeight,
        bodyShape: participantData.bodyShape,
        userName: participantData.userName,
        userEmail: participantData.userEmail,
      };
      // Include currentChannel from server if provided
      (newParticipant as any).currentChannel =
        participantData.currentChannel || "spatial";
      state.room.participants.set(
        participantData.participantId,
        newParticipant
      );
      emitEvent("new-participant", newParticipant);
    }
  });

  // ── Participant left ──────────────────────────────────────────────────────
  socket.on("participant-left", (data: {participantId: string}) => {
    if (state.room) {
      state.room.participants.delete(data.participantId);
      spatialAudioManager.removeParticipant(data.participantId);
      emitEvent("participant-left", data.participantId);
    }
  });

  // ── Consumer created (incoming media track) ───────────────────────────────
  socket.on(
    "consumer-created",
    async (data: {
      consumerId: string;
      producerId: string;
      kind: "audio" | "video";
      rtpParameters: any;
      participantId: string;
      conferenceId: string;
      roomId: string;
      position: Position;
      direction: Direction;
      mediaState: MediaState;
      timestamp: number;
      bodyHeight?: string;
      bodyShape?: string;
      userName?: string;
      userEmail?: string;
      currentChannel?: string;
    }) => {
      debugLog("CONSUMER", `Full data for ${data.userName}`, {
        participantId: data.participantId,
        position: data.position,
        channel: data.currentChannel,
        mediaState: data.mediaState,
      });

      let consumer: any;
      let track: MediaStreamTrack;
      try {
        const result = await mediasoupManager.consume(data);
        consumer = result.consumer;
        track = result.track;
      } catch (err: any) {
        console.warn(
          `[SDK] consume() skipped for ${data.participantId?.substring(0, 8)} — transport not ready (${err?.message}).`
        );
        return;
      }

      let participant = state.room?.participants.get(data.participantId);

      if (!participant && state.room) {
        participant = {
          participantId: data.participantId,
          userId: data.participantId.split(":")[0] || data.participantId,
          deviceId: data.participantId.split(":")[1] || data.participantId,
          isLocal: false,
          position: data.position,
          direction: data.direction,
          mediaState: data.mediaState,
          producers: new Map(),
          consumers: new Map(),
          bodyHeight: data.bodyHeight,
          bodyShape: data.bodyShape,
          userName: data.userName,
          userEmail: data.userEmail,
        };
        (participant as any).currentChannel = data.currentChannel || "spatial";
        state.room.participants.set(data.participantId, participant);
      }

      if (participant) {
        participant.position = data.position;
        participant.direction = data.direction;
        participant.mediaState = data.mediaState;
        (participant as any).currentChannel =
          data.currentChannel ||
          (participant as any).currentChannel ||
          "spatial";

        if (data.bodyHeight !== undefined)
          participant.bodyHeight = data.bodyHeight;
        if (data.bodyShape !== undefined)
          participant.bodyShape = data.bodyShape;
        if (data.userName !== undefined) participant.userName = data.userName;
        if (data.userEmail !== undefined)
          participant.userEmail = data.userEmail;

        participant.consumers.set(consumer.id, consumer);

        if (track.kind === "audio") {
          participant.audioTrack = track;

          const isLocalParticipant =
            participant.participantId === state.localParticipant?.participantId;

          if (isLocalParticipant) {
            return; // Never connect own audio — prevents loopback
          }

          const participantChannel =
            (participant as any).currentChannel || "spatial";
          const isInHuddle = participantChannel !== "spatial";

          await spatialAudioManager.setupSpatialAudioForParticipant(
            participant.participantId,
            track,
            isInHuddle
          );

          mediasoupManager
            .resumeConsumer(consumer.id)
            .then(() => {})
            .catch((err) => {
              console.error(`[SDK] Failed to resume consumer:`, err);
            });
        } else if (track.kind === "video") {
          const isScreenshare = (data as any).appData?.isScreenshare;

          if (isScreenshare) {
            (participant as any).screenshareTrack = track;
          } else {
            participant.videoTrack = track;
          }

          const resumeWithRetry = async (retries = 3) => {
            for (let i = 0; i < retries; i++) {
              try {
                await mediasoupManager.resumeConsumer(consumer.id);
                return;
              } catch (err) {
                if (i < retries - 1) {
                  await new Promise((resolve) => setTimeout(resolve, 200));
                } else {
                  console.error(
                    `[SDK] Failed to resume video consumer after ${retries} attempts:`,
                    err
                  );
                }
              }
            }
          };
          resumeWithRetry();
        }

        emitEvent("consumer-created", {
          participant,
          track,
          consumer,
          isScreenshare: (data as any).appData?.isScreenshare,
          appData: (data as any).appData,
        });
      }
    }
  );

  // ── Position updated ──────────────────────────────────────────────────────
  socket.on(
    "participant-position-updated",
    (data: {
      participantId: string;
      conferenceId: string;
      roomId: string;
      position: Position;
      direction: Direction;
      mediaState: MediaState;
      consumerIds: string[];
      timestamp: number;
      bodyHeight?: string;
      bodyShape?: string;
      userName?: string;
      userEmail?: string;
      currentChannel?: string;
      rot?: {x: number; y: number; z: number};
      pan?: number;
      dxLocal?: number;
    }) => {
      const participant = state.room?.participants.get(data.participantId);
      if (participant) {
        debugLog(
          "POSITION",
          `${data.userName || data.participantId.slice(0, 8)}`,
          {
            pos: {
              x: data.position?.x?.toFixed(1),
              y: data.position?.y?.toFixed(1),
            },
            channel: data.currentChannel,
          }
        );

        participant.position = data.position;
        participant.direction = data.direction;
        participant.mediaState = data.mediaState;
        if (data.bodyHeight !== undefined)
          participant.bodyHeight = data.bodyHeight;
        if (data.bodyShape !== undefined)
          participant.bodyShape = data.bodyShape;
        if (data.userName !== undefined) participant.userName = data.userName;
        if (data.userEmail !== undefined)
          participant.userEmail = data.userEmail;
        if (data.currentChannel !== undefined)
          participant.currentChannel = data.currentChannel;
        if (data.pan !== undefined) (participant as any).pan = data.pan;
        if (data.dxLocal !== undefined)
          (participant as any).dxLocal = data.dxLocal;
        if (data.rot !== undefined) (participant as any).rot = data.rot;

        const participantChannel = (participant as any).currentChannel;
        const isInHuddle = participantChannel !== "spatial";

        if (!isInHuddle) {
          spatialAudioManager.updateSpatialAudio(
            data.participantId,
            data.position,
            (data as any).rot || data.direction
          );
        }

        emitEvent("participant-position-updated", participant);
      }
    }
  );

  // ── Media state updated ───────────────────────────────────────────────────
  socket.on(
    "participant-media-state-updated",
    (data: {
      participantId: string;
      conferenceId: string;
      roomId: string;
      mediaState: MediaState;
      position: Position;
      direction: Direction;
      timestamp: number;
    }) => {
      const participant = state.room?.participants.get(data.participantId);
      if (participant) {
        participant.mediaState = data.mediaState;
        participant.position = data.position;
        participant.direction = data.direction;
        emitEvent("participant-media-state-updated", participant);
      }
    }
  );

  // ── Connection events ─────────────────────────────────────────────────────
  socket.on("error", (error: any) => {
    emitEvent("error", error);
  });

  socket.on("disconnect", () => {
    emitEvent("disconnected");
  });

  // ── Huddle events ─────────────────────────────────────────────────────────
  socket.on("huddle-invite-received", (data) => {
    emitEvent("huddle-invite-received", data);
  });

  socket.on("private-huddle-started", (data) => {
    if (state.localParticipant) {
      state.localParticipant.currentChannel = data.channelId;
    }
    emitEvent("private-huddle-started", data);
  });

  socket.on("huddle-updated", (data) => {
    emitEvent("huddle-updated", data);
  });

  socket.on("huddle-invite-rejected", (data) => {
    emitEvent("huddle-invite-rejected", data);
  });

  socket.on("huddle-ended", (data) => {
    if (state.localParticipant) {
      state.localParticipant.currentChannel = "spatial";
    }
    emitEvent("huddle-ended", data);
  });

  socket.on("participant-channel-changed", (data) => {
    const participant = state.room?.participants.get(data.participantId);
    if (participant) {
      participant.currentChannel = data.channelId;

      const isInSpatialChannel = data.channelId === "spatial";
      if (participant.audioTrack) {
        spatialAudioManager.setParticipantSpatialization(
          data.participantId,
          isInSpatialChannel
        );
      }

      const myChannel = state.localParticipant?.currentChannel || "spatial";
      const theirChannel = data.channelId || "spatial";
      if (myChannel !== theirChannel) {
        (participant as any).screenshareTrack = null;
      }
    }
    if (state.localParticipant?.participantId === data.participantId) {
      state.localParticipant!.currentChannel = data.channelId;
    }
    emitEvent("participant-channel-changed", data);
  });

  socket.on(
    "consumer-closed",
    (data: {
      consumerId: string;
      participantId: string;
      kind: string;
      isScreenshare?: boolean;
    }) => {
      emitEvent("consumer-closed", data);
    }
  );

  socket.on("mute-requested", (data) => {
    emitEvent("mute-requested", data);
  });

  // ── Space live broadcast events ───────────────────────────────────────────
  socket.on(
    "space-live-broadcast-available",
    (data: {
      roomId: string;
      producerId: string;
      producerUserId: string;
      userName: string;
    }) => {
      debugLog("SPACE-LIVE-BROADCAST", "Broadcast available", data);
      emitEvent("space-live-broadcast-available", data);
    }
  );

  socket.on("space-live-broadcast-stopped", (data: {roomId: string}) => {
    debugLog("SPACE-LIVE-BROADCAST", "Broadcast stopped", data);
    emitEvent("space-live-broadcast-stopped", data);
  });
}
